import React from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import LinkIcon from "@mui/icons-material/Link";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LibraryFilterChips from "./LibraryFilterChips";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import { formatDate } from "../requests/utilities/helpers";
import {
  API_get_learning_form_summaries,
  API_get_learning_form_detail,
  API_post_set_learning_asset_purpose,
} from "../../../constants";
import FormPublicReportModal from "./FormPublicReportModal";
import MoveAssetConfirm, { DESTINATIONS } from "./MoveAssetConfirm";

// Library copy differs by tab: Training Content lists "forms" (rubrics,
// checklists, sign-offs); the Engagement tab lists "surveys" (the same asset,
// shared by link). Everything else on the card is the same component.
const COPY = {
  training: {
    title: "Forms",
    blurb: "Reusable rubrics, checklists, note logs, and feedback forms",
    newLabel: "New Form",
    createTooltip: "Create new form",
    noun: "Form",
    plural: "forms",
    searchPlaceholder: "Search forms...",
    emptyBlurb: "Build reusable rubrics, checklists, note logs, and feedback forms.",
    emptyCreate: "Create your first form",
    noResults: "No forms match your filters",
    moveLabel: "Move to Engagement",
    movedToast: `Moved to ${DESTINATIONS.engagement.form}.`,
  },
  engagement: {
    title: "Surveys",
    blurb: "Surveys, polls and feedback forms, shared by link or QR code",
    newLabel: "New Survey",
    createTooltip: "Create new survey",
    noun: "Survey",
    plural: "surveys",
    searchPlaceholder: "Search surveys...",
    emptyBlurb: "Ask your team or your guests anything. Share a link or QR code and watch the responses roll in.",
    emptyCreate: "Create your first survey",
    noResults: "No surveys match your filters",
    moveLabel: "Move to Training",
    movedToast: `Moved to ${DESTINATIONS.training.form}.`,
  },
};

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
];

const SCORING_OPTIONS = [
  { value: "scored", label: "Scored" },
  { value: "not_scored", label: "Not scored" },
];

// Keyed on a LIVE link (public_active_links), not on whether the form has
// ever been shared: "Shared" answers "is this collecting right now".
const SHARING_OPTIONS = [
  { value: "shared", label: "Shared" },
  { value: "not_shared", label: "Not shared" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function FormCard({ form, onEdit, onResults, onMove, isEngagement, copy }) {
  const fieldCount = form.field_count || 0;
  const completedByLabel = isEngagement
    ? "Survey"
    : form.completed_by_role === "manager"
    ? "Manager-completed"
    : "Learner-completed";
  const updatedLabel = form.updated_at
    ? `Updated ${formatDate(form.updated_at)}`
    : null;
  const linkCount = form.public_active_links || 0;
  const responseCount = form.public_response_count || 0;

  return (
    <div
      className="group cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      onClick={onEdit}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600">
          <DescriptionOutlinedIcon className="text-white" style={{ fontSize: 20 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
                {form.name || "Untitled form"}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                <span>
                  {fieldCount} {fieldCount === 1 ? "field" : "fields"}
                </span>
                <span>·</span>
                <span>{completedByLabel}</span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              {isEngagement && (
                <Tooltip title="Results">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onResults();
                    }}
                    sx={{ color: "#475569" }}
                  >
                    <BarChartOutlinedIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={`Edit ${copy.noun.toLowerCase()}`}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                  sx={{ color: "#475569" }}
                >
                  <EditIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={copy.moveLabel}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMove();
                  }}
                  sx={{ color: "#475569" }}
                >
                  <DriveFileMoveOutlinedIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {form.description && (
            <p className="mt-2 line-clamp-2 text-xs text-slate-500">
              {form.description}
            </p>
          )}

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {form.scoring_enabled && (
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-violet-100">
                  Scored
                </span>
              )}
              {form.gate_on_pass && (
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
                  Requires pass
                </span>
              )}
              {form.is_directly_assignable && !isEngagement && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                  Assignable
                </span>
              )}
              {/* The counts ride INSIDE the badge rather than in the facts
                  line above: responses are the number a manager opens this
                  page for, and next to "Shared" they read as that link's
                  result instead of as one more attribute of the form. A link
                  count only earns its place once there's more than one, which
                  is the per-store survey case. Responses are cumulative, so
                  they stay put when a link is switched off; the badge itself
                  is live state: once a link is switched off the badge goes
                  grey and reads "Sharing off", so the responses it already
                  collected stay on the card instead of disappearing with it. */}
              {/* Training cards only flag a still-live legacy link (a nudge
                  to move the form); the Engagement card is the full badge. */}
              {(isEngagement ? linkCount > 0 || responseCount > 0 : linkCount > 0) && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${
                    linkCount > 0
                      ? "bg-sky-50 text-sky-700 ring-sky-100"
                      : "bg-slate-100 text-slate-500 ring-slate-200"
                  }`}
                >
                  <LinkIcon style={{ fontSize: 14 }} />
                  {linkCount > 0 ? "Shared" : "Sharing off"}
                  {linkCount > 1 && (
                    <>
                      <span className="text-sky-300">·</span>
                      <span className="tabular-nums">{linkCount} links</span>
                    </>
                  )}
                  {responseCount > 0 && (
                    <>
                      <span className={linkCount > 0 ? "text-sky-300" : "text-slate-300"}>·</span>
                      <span className="tabular-nums">
                        {responseCount} {responseCount === 1 ? "response" : "responses"}
                      </span>
                    </>
                  )}
                </span>
              )}
            </div>
            {updatedLabel && (
              <span className="shrink-0 text-xs text-slate-400">{updatedLabel}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FormsDisplay(props) {
  const { homeCompany, onCreate, onEdit, onChanged } = props;
  const purpose = props.purpose === "engagement" ? "engagement" : "training";
  const isEngagement = purpose === "engagement";
  const copy = COPY[purpose];
  const [forms, setForms] = React.useState(null);
  // Results modal opened straight from a card (Engagement). The report needs
  // the form's share links, which only the detail endpoint returns.
  const [reportTarget, setReportTarget] = React.useState(null); // {uuid,name,shares}
  const [movingUuid, setMovingUuid] = React.useState(null);
  // Training -> Engagement resets scoring / grading / role config on the
  // server; ask first when there's something to lose.
  const [pendingMove, setPendingMove] = React.useState(null); // form
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [completionFilter, setCompletionFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sharingFilter, setSharingFilter] = React.useState("all");
  const [scoringFilter, setScoringFilter] = React.useState("all");

  const fetchForms = React.useCallback(async () => {
    if (!homeCompany) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_learning_form_summaries, {
        company_id: homeCompany,
        purpose,
      });
      setForms(res.data?.redux?.payload?.learning_forms || []);
    } catch (err) {
      toast.error(`Couldn't load ${copy.plural}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [homeCompany, purpose, copy.plural]);

  const openResults = async (form) => {
    try {
      const res = await getRightworkAPI(API_get_learning_form_detail, {
        company_id: homeCompany,
        learning_form_uuid: form.uuid,
      });
      const detail = res.data?.redux?.payload?.learning_form;
      setReportTarget({
        uuid: form.uuid,
        name: detail?.name || form.name,
        shares: Array.isArray(detail?.public_shares) ? detail.public_shares : [],
      });
    } catch (err) {
      toast.error("Couldn't load results. Please try again.");
    }
  };

  // Every move confirms and names the destination tab (the form vanishes from
  // this list). Moving INTO Engagement is also a one-way conversion for a
  // form that carries training config, so the dialog lists what is reset.
  const requestMove = (form) => setPendingMove(form);
  const moveLosses = (form) => {
    if (isEngagement) return [];
    const out = [];
    if (form.completed_by_role === "manager") out.push("manager completion (it becomes self-filled)");
    if (form.scoring_enabled) out.push("scoring, points on every field, and answer keys");
    if (form.gate_on_pass) out.push("the pass requirement");
    if (form.save_to_profile) out.push("keeping a copy on the employee's profile");
    return out;
  };

  // Move between the Training and Engagement tabs. The server refuses a move
  // into Engagement while the form is still embedded in a section or directly
  // assignable, and says which sections.
  const moveForm = async (form) => {
    if (movingUuid) return;
    setPendingMove(null);
    setMovingUuid(form.uuid);
    try {
      await postRightworkAPI(API_post_set_learning_asset_purpose, {
        company_id: homeCompany,
        asset_type: "form",
        asset_uuid: form.uuid,
        purpose: isEngagement ? "training" : "engagement",
      });
      toast.success(copy.movedToast, { autoClose: 2500 });
      if (onChanged) onChanged();
      else fetchForms();
    } catch (err) {
      const message = err?.response?.data?.system?.message;
      toast.error(message || "Couldn't move this form. Please try again.");
    } finally {
      setMovingUuid(null);
    }
  };

  React.useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const filteredForms = React.useMemo(() => {
    const list = forms || [];
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = list.filter((form) => {
      const haystack = `${form.name || ""} ${form.description || ""}`.toLowerCase();
      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (completionFilter !== "all" && form.completed_by_role !== completionFilter) {
        return false;
      }
      if (assignableFilter === "assignable" && !form.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && form.is_directly_assignable) {
        return false;
      }
      if (scoringFilter === "scored" && !form.scoring_enabled) {
        return false;
      }
      if (scoringFilter === "not_scored" && form.scoring_enabled) {
        return false;
      }
      if (sharingFilter === "shared" && !form.public_active_links) {
        return false;
      }
      if (sharingFilter === "not_shared" && form.public_active_links) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "fields") {
        return (b.field_count || 0) - (a.field_count || 0);
      }
      if (sortKey === "responses") {
        return (b.public_response_count || 0) - (a.public_response_count || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [
    forms,
    searchInput,
    completionFilter,
    assignableFilter,
    scoringFilter,
    sharingFilter,
    sortKey,
  ]);

  const activeFilterCount =
    (completionFilter !== "all" ? 1 : 0) +
    (assignableFilter !== "all" ? 1 : 0) +
    (scoringFilter !== "all" ? 1 : 0) +
    (sharingFilter !== "all" ? 1 : 0);
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setCompletionFilter("all");
    setAssignableFilter("all");
    setScoringFilter("all");
    setSharingFilter("all");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="mb-1 text-2xl font-bold text-slate-900">{copy.title}</h1>
              <p className="text-sm text-slate-600">{copy.blurb}</p>
            </div>
            <Tooltip title={copy.createTooltip}>
              <button
                type="button"
                onClick={() => onCreate && onCreate()}
                className="flex items-center gap-2 rounded-lg bg-violet-700 px-6 py-3 text-white shadow-sm transition-colors hover:bg-violet-800"
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">{copy.newLabel}</span>
              </button>
            </Tooltip>
          </div>

          <div className="mb-6 flex items-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <DescriptionOutlinedIcon style={{ fontSize: "18px" }} />
              <span>
                {filteredForms.length}{" "}
                {filteredForms.length === 1 ? copy.noun : copy.title}
              </span>
            </div>
            {searchInput && (
              <div className="flex items-center gap-2 text-violet-600">
                <SearchIcon style={{ fontSize: "16px" }} />
                <span>Showing search results</span>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 text-violet-600">
                <FilterListIcon style={{ fontSize: "16px" }} />
                <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-md">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={copy.searchPlaceholder}
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>

            {!isEngagement && (
              <Select
                size="small"
                sx={filterSelectSx}
                value={completionFilter}
                onChange={(event) => setCompletionFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All completion types
                </MenuItem>
                <MenuItem value="employee" sx={{ fontSize: 14 }}>
                  Learner-completed
                </MenuItem>
                <MenuItem value="manager" sx={{ fontSize: 14 }}>
                  Manager-completed
                </MenuItem>
              </Select>
            )}

            <Select
              size="small"
              sx={filterSelectSx}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value)}
            >
              <MenuItem value="recent" sx={{ fontSize: 14 }}>
                Recently updated
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: 14 }}>
                Name
              </MenuItem>
              <MenuItem value="fields" sx={{ fontSize: 14 }}>
                Most fields
              </MenuItem>
              {isEngagement && (
                <MenuItem value="responses" sx={{ fontSize: 14 }}>
                  Most responses
                </MenuItem>
              )}
            </Select>
          </div>

          <div className="mt-4">
            <LibraryFilterChips
              groups={[
                // Assignability and scoring don't exist on surveys.
                ...(isEngagement
                  ? []
                  : [
                      {
                        key: "assignable",
                        value: assignableFilter,
                        onChange: setAssignableFilter,
                        options: ASSIGNABLE_OPTIONS,
                      },
                      {
                        key: "scoring",
                        value: scoringFilter,
                        onChange: setScoringFilter,
                        options: SCORING_OPTIONS,
                      },
                    ]),
                ...(isEngagement
                  ? [
                      {
                        key: "sharing",
                        value: sharingFilter,
                        onChange: setSharingFilter,
                        options: SHARING_OPTIONS,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadSpinner />
          </div>
        ) : filteredForms.length === 0 ? (
          <TrainingEmptyState
            icon={DescriptionOutlinedIcon}
            plural={copy.plural}
            blurb={copy.emptyBlurb}
            createLabel={copy.emptyCreate}
            searching={hasSearchOrFilters}
            noResultsTitle={copy.noResults}
            noResultsBlurb="Try adjusting your search or clearing the selected filters."
            clearLabel="Clear filters"
            onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
            onCreate={() => onCreate && onCreate()}
          />
        ) : (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-1">
            {filteredForms.map((form) => (
              <FormCard
                key={form.uuid}
                form={form}
                isEngagement={isEngagement}
                copy={copy}
                onEdit={() => onEdit && onEdit(form)}
                onResults={() => openResults(form)}
                onMove={() => requestMove(form)}
              />
            ))}
          </div>
        )}
      </div>

      {pendingMove && (
        <MoveAssetConfirm
          name={pendingMove.name}
          assetType="form"
          toPurpose={isEngagement ? "training" : "engagement"}
          losses={moveLosses(pendingMove)}
          onCancel={() => setPendingMove(null)}
          onConfirm={() => moveForm(pendingMove)}
        />
      )}

      {isEngagement && (
        <FormPublicReportModal
          key={reportTarget?.uuid || "none"}
          open={!!reportTarget}
          onClose={() => setReportTarget(null)}
          companyId={homeCompany}
          formUuid={reportTarget?.uuid}
          formName={reportTarget?.name}
          shares={reportTarget?.shares || []}
        />
      )}
    </div>
  );
}

export default FormsDisplay;
